// src/lib/jumpDrag.ts
/** 
 * Utilities for dragging jump indicators - T-13 enhanced functionality
 */

export interface DragState {
  /** Whether a drag gesture is currently active */
  isDragging: boolean;
  /** ID of the jump being dragged (if any) */
  jumpId: string | null;
  /** Which part of the jump is being dragged */
  handleType: 'start' | 'end' | 'whole' | null;
  /** Mouse/touch position when the drag started */
  startMousePos: { x: number; y: number } | null;
  /**
   * Value on the number line under the pointer when the drag started.
   * Used so the pointer stays attached to the same logical point.
   */
  startValue: number | null;
  /**
   * Original jump start/end values at drag start.
   * We always apply the drag delta relative to these to avoid
   * accumulating rounding error from previews.
   */
  originalStartValue: number | null;
  originalEndValue: number | null;
  /** Pixel offset accumulated during drag (not currently used for layout) */
  dragOffset: { x: number; y: number };
}

export const initialDragState: DragState = {
  isDragging: false,
  jumpId: null,
  handleType: null,
  startMousePos: null,
  startValue: null,
  originalStartValue: null,
  originalEndValue: null,
  dragOffset: { x: 0, y: 0 }
};
  
  /**
   * Convert mouse position to number line value
   */
  export function mouseToValue(
    mouseX: number,
    mouseY: number,
    svgRect: DOMRect,
    pixelToValue: (px: number) => number,
    isHorizontal: boolean
  ): number {
    if (isHorizontal) {
      const relativeX = mouseX - svgRect.left;
      return pixelToValue(relativeX);
    } else {
      const relativeY = mouseY - svgRect.top;
      return pixelToValue(relativeY);
    }
  }
  
  /**
   * Get mouse position from event (handles both mouse and touch)
   */
  export function getEventPosition(event: MouseEvent | TouchEvent): { x: number; y: number } {
    if ('touches' in event && event.touches.length > 0) {
      return { x: event.touches[0].clientX, y: event.touches[0].clientY };
    } else if ('clientX' in event) {
      return { x: event.clientX, y: event.clientY };
    } else {
      return { x: 0, y: 0 };
    }
  }
  
  /**
   * Calculate new jump endpoints during drag
   */
  export function calculateDragEndpoints(
    originalJump: { startValue: number; endValue: number },
    handleType: 'start' | 'end' | 'whole',
    newValue: number
  ): { startValue: number; endValue: number } {
    if (handleType === 'start') {
      return {
        startValue: newValue,
        endValue: originalJump.endValue
      };
    } else if (handleType === 'end') {
      return {
        startValue: originalJump.startValue,
        endValue: newValue
      };
    } else {
      // 'whole' drag - maintain the same span but move both endpoints
      const span = originalJump.endValue - originalJump.startValue;
      return {
        startValue: newValue,
        endValue: newValue + span
      };
    }
  }
  
  /**
   * Check if drag resulted in direction change
   */
  export function getDragDirection(startValue: number, endValue: number): 'positive' | 'negative' {
    return endValue >= startValue ? 'positive' : 'negative';
  }
  
  /**
   * Apply constraints to drag value (optional bounds checking)
   */
  export function constrainDragValue(
    value: number,
    bounds?: { min: number; max: number }
  ): number {
    if (!bounds) return value;
    
    return Math.max(bounds.min, Math.min(bounds.max, value));
  }
  
  /**
   * Round value to nice increments during drag (for smoother UX)
   */
  export function snapDragValue(
    value: number,
    snapIncrement: number = 0.25
  ): number {
    return Math.round(value / snapIncrement) * snapIncrement;
  }
  
  /**
   * Create drag preview data
   */
  export function createDragPreview(
    jump: { id: string; startValue: number; endValue: number },
    newStartValue: number,
    newEndValue: number
  ) {
    const direction = getDragDirection(newStartValue, newEndValue);
    const color = direction === 'positive' ? '#22c55e' : '#ef4444';
    
    return {
      ...jump,
      startValue: newStartValue,
      endValue: newEndValue,
      direction,
      color,
      isDragPreview: true
    };
  }
  
  /**
   * Debounce drag updates for performance
   */
  export function createDragDebouncer(callback: Function, delay: number = 16) {
    let timeoutId: number | null = null;
    
    return (...args: any[]) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      timeoutId = window.setTimeout(() => {
        callback(...args);
      }, delay);
    };
  }
  
  /**
   * Calculate cursor style based on drag state and handle type
   */
  export function getDragCursor(
    isDragging: boolean,
    handleType: 'start' | 'end' | 'whole' | null,
    isHovering: boolean
  ): string {
    if (isDragging) {
      return 'grabbing';
    } else if (isHovering || handleType) {
      return handleType === 'whole' ? 'move' : 'grab';
    } else {
      return 'pointer';
    }
  }
  
  /**
   * Validate drag operation
   */
  export function isValidDrag(
    startPos: { x: number; y: number },
    currentPos: { x: number; y: number },
    threshold: number = 5
  ): boolean {
    const distance = Math.sqrt(
      Math.pow(currentPos.x - startPos.x, 2) + 
      Math.pow(currentPos.y - startPos.y, 2)
    );
    
    return distance >= threshold;
  }
  
  /**
   * Format drag tooltip text
   */
  export function getDragTooltip(
    jump: { startValue: number; endValue: number },
    handleType: 'start' | 'end' | 'whole'
  ): string {
    const span = jump.endValue - jump.startValue;
    const direction = span >= 0 ? '+' : '';
    
    if (handleType === 'whole') {
      return `Drag to move entire jump\nCurrent span: ${direction}${span.toFixed(2)}`;
    } else {
      return `Drag to move ${handleType} point\nCurrent span: ${direction}${span.toFixed(2)}`;
    }
  }